import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getWeChatOpenIdFromUrl } from '../services/authService';
import { simpleRegister } from '../services/registerService';
import { uploadAndParseBusinessLicense, extractBusinessLicenseInfo } from '../services/uploadService';
import { useAuth } from '../contexts/AuthContext';
import './ModernLogin.css';

interface SimpleRegisterPageProps {
    onRegisterSuccess?: (data: any) => void;
}

interface FormData {
    companyName: string;
    businessLicenseNumber: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    contactPosition: string;
    businessLicense: File | null;
}

const SimpleRegisterPage: React.FC<SimpleRegisterPageProps> = ({ onRegisterSuccess }) => {
    const [formData, setFormData] = useState<FormData>({
        companyName: '',
        businessLicenseNumber: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        contactPosition: '',
        businessLicense: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [parseProgress, setParseProgress] = useState<any>(null);
    const [error, setError] = useState('');
    const [weChatOpenId, setWeChatOpenId] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [businessLicenseUrl, setBusinessLicenseUrl] = useState<string>('');

    const navigate = useNavigate();
    const location = useLocation();
    const { login: wechatLogin, refreshUserInfo, isLoggedIn, isLoading: authLoading, weChatOpenId: authContextOpenId } = useAuth();

    useEffect(() => {
        // 优先从 AuthContext 获取，其次从 URL，最后从 localStorage
        const urlOpenId = getWeChatOpenIdFromUrl();
        const storedOpenId = localStorage.getItem('weChatOpenId') || '';
        const openId = authContextOpenId || urlOpenId || storedOpenId;

        console.log('SimpleRegisterPage - 获取 weChatOpenId:', {
            authContextOpenId,
            urlOpenId,
            storedOpenId,
            finalOpenId: openId
        });

        setWeChatOpenId(openId);

        // 添加body类名防止滚动
        document.body.classList.add('login-page');
        document.documentElement.classList.add('login-page');

        // 清理函数
        return () => {
            document.body.classList.remove('login-page');
            document.documentElement.classList.remove('login-page');
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [authContextOpenId, previewUrl]); // 添加依赖项

    // 如果用户已登录，跳转到首页
    useEffect(() => {
        if (isLoggedIn && !authLoading) {
            console.log('用户已登录，准备跳转');
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUrl = urlParams.get('redirect');
            const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';

            setTimeout(() => {
                window.location.href = targetUrl;
            }, 100);
        }
    }, [isLoggedIn, authLoading]);

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // 验证文件类型
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setError('请上传 JPG、PNG 或 PDF 格式的营业执照');
                return;
            }

            // 验证文件大小 (200MB - MinerU限制)
            if (file.size > 200 * 1024 * 1024) {
                setError('文件大小不能超过 200MB');
                return;
            }

            setFormData(prev => ({ ...prev, businessLicense: file }));
            setError('');
            setIsParsing(true);
            setParseProgress(null);

            // 生成预览
            if (file.type.startsWith('image/')) {
                if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                }
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
            }

            try {
                // 上传并解析营业执照（直接返回解析结果）
                console.log('开始上传并解析营业执照...');
                const uploadResult = await uploadAndParseBusinessLicense(file, weChatOpenId);

                if (!uploadResult.success) {
                    throw new Error(uploadResult.message || '上传失败');
                }

                console.log('上传解析结果:', uploadResult);

                // 保存文件URL
                if (uploadResult.fileUrl) {
                    setBusinessLicenseUrl(uploadResult.fileUrl);
                }

                // 直接从响应中获取解析结果（state === 'done' 表示已完成）
                if (uploadResult.state === 'done' && uploadResult.structuredData) {
                    console.log('解析完成，结构化数据:', uploadResult.structuredData);
                    const extractedInfo = extractBusinessLicenseInfo(uploadResult.structuredData);

                    if (extractedInfo.companyName || extractedInfo.businessLicenseNumber) {
                        setFormData(prev => ({
                            ...prev,
                            companyName: extractedInfo.companyName || prev.companyName,
                            businessLicenseNumber: extractedInfo.businessLicenseNumber || prev.businessLicenseNumber,
                        }));

                        console.log('已自动填充:', extractedInfo);

                        if (extractedInfo.companyName && extractedInfo.businessLicenseNumber) {
                            setError(''); // 清除错误
                        }
                    }
                } else if (uploadResult.state === 'failed') {
                    throw new Error('解析失败，请重新上传');
                }

                setIsParsing(false);
                setParseProgress(null);


            } catch (err) {
                console.error('解析营业执照失败:', err);
                setIsParsing(false);
                setParseProgress(null);
                setError(err instanceof Error ? err.message : '解析失败，请手动填写信息');
            }
        }
    };

    const validateEmail = (email: string): boolean => {
        if (!email) return true; // 可选字段
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string): boolean => {
        const phoneRegex = /^1[3-9]\d{9}$/;
        return phoneRegex.test(phone);
    };

    const validateBusinessLicense = (number: string): boolean => {
        // 统一社会信用代码为18位
        return /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/.test(number);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // 验证必填项
            if (!formData.businessLicense) {
                throw new Error('请上传营业执照');
            }
            if (!formData.companyName.trim()) {
                throw new Error('请输入公司名称');
            }
            if (!formData.businessLicenseNumber.trim()) {
                throw new Error('请输入统一社会信用代码');
            }
            if (!validateBusinessLicense(formData.businessLicenseNumber)) {
                throw new Error('请输入正确的统一社会信用代码（18位）');
            }
            if (!formData.contactName.trim()) {
                throw new Error('请输入您的姓名');
            }
            if (!formData.contactPhone.trim()) {
                throw new Error('请输入联系电话');
            }
            if (!validatePhone(formData.contactPhone)) {
                throw new Error('请输入有效的手机号码');
            }

            if (!weChatOpenId) {
                throw new Error('未获取到微信授权信息，请重新授权');
            }

            // 验证可选但有格式要求的字段
            if (formData.contactEmail && !validateEmail(formData.contactEmail)) {
                throw new Error('请输入有效的邮箱地址');
            }

            // 使用已保存的营业执照URL（上传时已获取）
            const businessLicenseImageUrl = businessLicenseUrl;

            if (!businessLicenseImageUrl) {
                throw new Error('营业执照URL获取失败，请重新上传');
            }

            console.log('准备提交用户档案，营业执照URL:', businessLicenseImageUrl);

            // 提交用户档案
            const response = await simpleRegister({
                wechatOpenId: weChatOpenId,
                companyName: formData.companyName.trim(),
                businessLicenseNumber: formData.businessLicenseNumber.trim(),
                contactName: formData.contactName.trim(),
                contactPhone: formData.contactPhone.trim(),
                contactEmail: formData.contactEmail.trim(),
                contactPosition: formData.contactPosition.trim(),
                businessLicenseImageUrl: businessLicenseImageUrl,
            });

            if (response.success) {
                console.log('提交成功:', response.userProfile);

                // 资料提交成功后，尝试微信登录
                console.log('开始微信登录...');
                const wechatLoginSuccess = await wechatLogin();

                if (wechatLoginSuccess) {
                    console.log('微信登录成功，准备跳转');

                    // 刷新用户信息
                    await refreshUserInfo();

                    if (onRegisterSuccess) {
                        onRegisterSuccess(response.userProfile);
                    }

                    // 获取重定向URL
                    const urlParams = new URLSearchParams(location.search);
                    const redirectUrl = urlParams.get('redirect');
                    const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';

                    console.log('准备跳转到:', targetUrl);
                    navigate(targetUrl, { replace: true });
                } else {
                    throw new Error('资料提交成功，但登录失败，请刷新页面重试');
                }
            } else {
                throw new Error(response.message || '提交失败');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '提交失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen bg-gray-100 flex flex-col modern-login-container modern-login-safe-area overflow-hidden">
            {/* Logo 和标题区域 */}
            <div className="flex-shrink-0 px-6 pt-4 pb-3">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    供应商资料
                </h1>
                <p className="text-gray-600 text-sm">
                    提交资料后即可进入系统
                </p>
            </div>

            {/* 表单区域 - 可滚动 */}
            <div className="flex-1 px-6 overflow-y-auto pb-24">
                <form onSubmit={handleSubmit} className="space-y-5">

                    {/* 错误提示 */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 error-slide-in">
                            <p className="text-red-600 text-sm font-medium">{error}</p>
                        </div>
                    )}

                    {/* 营业执照上传 - 必填 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            营业执照 <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                            <input
                                type="file"
                                id="businessLicense"
                                accept="image/jpeg,image/png,image/jpg,application/pdf"
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={isParsing}
                            />
                            <label
                                htmlFor="businessLicense"
                                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-3xl ${isParsing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-100'} bg-gray-50 transition-all`}
                            >
                                {isParsing ? (
                                    <div className="flex flex-col items-center">
                                        <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <p className="mt-2 text-sm text-blue-600">正在解析营业执照...</p>
                                        {parseProgress && (
                                            <p className="text-xs text-gray-500">
                                                进度: {parseProgress.current || 0}/{parseProgress.total || 0} ({parseProgress.percentage || 0}%)
                                            </p>
                                        )}
                                    </div>
                                ) : previewUrl ? (
                                    <div className="flex flex-col items-center">
                                        <img src={previewUrl} alt="营业执照预览" className="h-24 object-contain" />
                                        <p className="mt-2 text-xs text-gray-500">{formData.businessLicense?.name}</p>
                                    </div>
                                ) : formData.businessLicense ? (
                                    <div className="flex flex-col items-center">
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="mt-2 text-xs text-gray-500">{formData.businessLicense.name}</p>
                                    </div>
                                ) : (
                                    <>
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        <p className="mt-2 text-sm text-gray-500">点击上传营业执照</p>
                                        <p className="text-xs text-gray-400">支持 JPG、PNG、PDF，自动识别填充</p>
                                    </>
                                )}
                            </label>
                        </div>
                    </div>

                    {/* 公司名称 - 必填 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            公司名称 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入公司名称"
                            required
                        />
                    </div>

                    {/* 统一社会信用代码 - 必填 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            统一社会信用代码 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.businessLicenseNumber}
                            onChange={(e) => handleInputChange('businessLicenseNumber', e.target.value.toUpperCase())}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入18位统一社会信用代码"
                            maxLength={18}
                            required
                        />
                        <p className="mt-1 text-xs text-gray-500">请输入营业执照上的18位统一社会信用代码</p>
                    </div>

                    {/* 姓名 - 必填 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            您的姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.contactName}
                            onChange={(e) => handleInputChange('contactName', e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入您的姓名"
                            required
                        />
                    </div>

                    {/* 联系电话 - 必填 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            联系电话 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            value={formData.contactPhone}
                            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入联系电话"
                            maxLength={11}
                            required
                        />
                    </div>

                    {/* 邮箱 - 可选 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            邮箱地址
                        </label>
                        <input
                            type="email"
                            value={formData.contactEmail}
                            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入邮箱地址（可选）"
                        />
                    </div>

                    {/* 公司职位 - 可选 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            公司职位
                        </label>
                        <input
                            type="text"
                            value={formData.contactPosition}
                            onChange={(e) => handleInputChange('contactPosition', e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入您的职位（可选）"
                        />
                    </div>

                </form>
            </div>

            {/* 提交按钮 - 固定在底部 */}
            <div className="flex-shrink-0 px-6 pb-6 pt-4 bg-white border-t border-gray-200">
                <button
                    type="submit"
                    disabled={isLoading}
                    onClick={handleSubmit}
                    className="w-full bg-blue-600 text-white py-4 px-4 rounded-3xl text-base font-semibold hover:bg-blue-700 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 modern-login-button shadow-lg"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            提交中...
                        </div>
                    ) : (
                        '提交资料'
                    )}
                </button>
            </div>

        </div>
    );
};

export default SimpleRegisterPage;
